import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../lib/api";

export default function NotificationBell({ testIdPrefix = "notif" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = () => {
    api.get("/me/notifications").then(r => {
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const onItemClick = async (n) => {
    if (!n.is_read) {
      await api.post(`/me/notifications/${n.id}/read`).catch(() => {});
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    await api.post("/me/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`${testIdPrefix}-bell`}
        className="relative p-2 rounded-lg text-[#1a3c5e] hover:bg-[#FEF3E2]"
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid={`${testIdPrefix}-unread-badge`}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F7941D] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-[#1a3c5e]/10 shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="font-display font-bold text-[#1a3c5e]">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} data-testid={`${testIdPrefix}-mark-all`} className="text-xs text-[#F7941D] font-semibold hover:underline flex items-center gap-1">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">No notifications yet.</div>
            ) : items.map(n => (
              <button
                key={n.id}
                onClick={() => onItemClick(n)}
                data-testid={`${testIdPrefix}-item-${n.id}`}
                className={`w-full text-left px-4 py-3 hover:bg-[#FEF3E2]/40 ${!n.is_read ? "bg-[#FEF3E2]/20" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#F7941D] mt-1.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1a3c5e] text-sm">{n.title}</div>
                    <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">{n.message}</div>
                    <div className="text-[10px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
